import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { QuickAnswerSection } from '@/components/QuickAnswerSection';
import { GateFAQSection } from '@/components/GateFAQSection';
import { Footer } from '@/components/Footer';
import { featuresPillarAIO } from '@/data/featuresPillarAIO';
import { loadAllArticles } from '@/services/articleLoader';
import { CTA1 } from '@/components/CTABanners';

const FEATURES_PILLAR_SLUG = 'features-guide-2026';

export const FeaturesPillarPage = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const articles = await loadAllArticles();
        const found = articles.find(a => a.slug === FEATURES_PILLAR_SLUG);
        if (mounted) {
          setArticle(found || null);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Failed to load features guide pillar from /articles:', e);
        if (mounted) {
          setArticle(null);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const quickAnswer = article?.quickAnswer || featuresPillarAIO.quickAnswer;
  const faqItems = article?.faqItems && article.faqItems.length ? article.faqItems : featuresPillarAIO.faqItems;

  if (loading && !article) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading guide...
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article?.title || 'IPTV Premium Features Guide 2026 | XtreamGate'}</title>
        <meta
          name="description"
          content={
            article?.excerpt ||
            'Overview of XtreamCube IPTV premium features in 2026 – 20,000+ channels, 4K/HDR, catch‑up TV, cloud DVR, multi‑device streaming and more.'
          }
        />
        <link rel="canonical" href="https://xtreamgate.com/guides/features" />
      </Helmet>

      <div className="min-h-screen relative">
        <GridBackground />
        <MainNavigation />

        <main className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <Button asChild variant="outline" className="mb-8 glass border-primary/30">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>

            {/* Page Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <Zap className="w-10 h-10 text-primary" />
                <h1 className="text-4xl sm:text-5xl font-bold text-white">
                  {article?.title || 'Premium Features'}
                </h1>
              </div>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                {article?.excerpt || 'Everything you need for the ultimate IPTV experience'}
              </p>
            </motion.div>

            {/* Quick Answer */}
            <QuickAnswerSection answer={quickAnswer} />

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12"
            >
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Key Features
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {featuresPillarAIO.keyFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                    className="glass rounded-xl p-6"
                    style={{ boxShadow: 'var(--shadow-glass)' }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">{feature.icon}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-white">{feature.name}</h3>
                          {feature.premium && (
                            <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                              Premium
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white/70 mb-3">{feature.description}</p>
                        <div className="space-y-1">
                          {feature.included.map(item => (
                            <div key={item} className="flex items-center gap-2 text-xs text-white/60">
                              <CheckCircle2 className="w-3 h-3 text-primary flex-shrink-0" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Comparison Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 glass rounded-xl p-6 md:p-8 overflow-x-auto"
              style={{ boxShadow: 'var(--shadow-glass)' }}
            >
              <h2 className="text-2xl font-bold text-white mb-6 text-center">
                Plans Comparison
              </h2>
              <div className="min-w-[600px]">
                <div className="grid grid-cols-4 gap-4 mb-4 pb-4 border-b border-white/10">
                  {featuresPillarAIO.comparisonTable.headers.map((header, i) => (
                    <div key={i} className={`font-bold ${i === 0 ? 'text-white' : 'text-center text-primary'}`}>
                      {header}
                    </div>
                  ))}
                </div>
                {featuresPillarAIO.comparisonTable.rows.map((row, i) => (
                  <div key={i} className="grid grid-cols-4 gap-4 py-3 border-b border-white/5">
                    {row.map((cell, j) => (
                      <div key={j} className={`text-sm ${j === 0 ? 'text-white/90 font-medium' : 'text-center text-white/70'}`}>
                        {cell}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* FAQ Section */}
            <div className="mt-16">
              <GateFAQSection faqItems={faqItems} />
            </div>

            {/* CTA */}
            <div className="mt-16">
              <CTA1
                headline="Experience Premium Features"
                subtext="Test all premium features free for 24 hours"
                buttonText="Start Free Trial"
                buttonUrl="https://xtreamcube.shop/freetrialiptv/"
                showIcon={false}
              />
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};
